import {handleMultilingual} from '@wix/wixstores-client-storefront-sdk';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {IWidgetController} from '@wix/native-components-infra/dist/src/types/types';
import {ControllerConstructorType} from '../../types/app.types';
import {CartController} from './CartController';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {SPECS} from '../../domain/specs';

export const controllerFactory =
  <T extends CartController>(ControllerConstructor: ControllerConstructorType<T>) =>
  (controllerParams: ControllerParams): IWidgetController => {
    const siteStore = controllerParams?.appData?.context.siteStore;
    let controller: T | undefined;

    const getController = async () => {
      if (controller) {
        return controller;
      }
      await siteStore.loadClientConfig();
      controller = new ControllerConstructor(controllerParams);
      return controller;
    };

    return {
      async pageReady() {
        try {
          await getController();

          const isMultilingualNeeded = () => {
            const viewerContext = new ViewerContext(
              controllerParams.controllerConfig,
              controllerParams.controllerConfig.essentials.experiments.enabled(SPECS.MoveCartToViewerContext)
                ? undefined
                : siteStore
            );

            if (viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext)) {
              return controllerParams.controllerConfig.config.externalId && !viewerContext.isPrimaryLanguage();
            }

            const multilingualFields = siteStore.getMultiLangFields();
            return (
              controllerParams.controllerConfig.config.externalId &&
              multilingualFields &&
              !multilingualFields?.isPrimaryLanguage
            );
          };

          const shouldCallAppSettings = isMultilingualNeeded();

          await Promise.all([
            ...(shouldCallAppSettings ? [handleMultilingual(controllerParams, controller.getFreeTexts())] : []),
            controller.init(),
          ]);
          controllerParams.controllerConfig.wixCodeApi.location.onChange(() => void controller.load());
        } catch (error) {
          console.error(error);
          controllerParams.controllerConfig.setProps({generalError: true});
          controllerParams.appData.reportError(error);
        }
      },
      exports() {
        return getExportsAfterControllerLoaded(getController, ['hideSecureCheckout', 'showSecureCheckout']);
      },
      updateConfig: (_$w, updatedConfig: {[key: string]: any}): void => {
        return void controller?.onStyleUpdate?.(updatedConfig.style.styleParams);
      },
    };
  };

function getExportsAfterControllerLoaded(getController: () => Promise<CartController>, keys: string[]) {
  return keys.reduce((acc, key) => {
    acc[key] = async (...args: any[]) => {
      const controller = await getController();
      return controller.exports()[key](...args);
    };
    return acc;
  }, {});
}
